import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { RETURN_ADDRESS, CONTACT_INFO, siteImages } from '../data/mock';
import { RotateCcw, Package, Clock, CheckCircle, XCircle, AlertCircle, ArrowRight, MapPin } from 'lucide-react';

const ReturnsPage = () => {
  useEffect(() => {
    document.title = 'Returns & Refund Policy | Keto Vybe';
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.returnsBanner}
            alt="Returns"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Policy</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                Returns & Refunds
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                30-day hassle-free return policy
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          {/* Return Address Card */}
          <div className="bg-gradient-to-br from-vybe-600 to-vybe-700 rounded-3xl p-8 mb-12">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-white mb-2">Return Address</h2>
                <p className="text-vybe-100 text-lg leading-relaxed">
                  {RETURN_ADDRESS.street}<br />
                  {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}<br />
                  {RETURN_ADDRESS.country}
                </p>
              </div>
            </div>
          </div>

          {/* Policy Overview */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Clock, title: '30-Day Window', desc: 'Return within 30 days', color: 'vybe' },
              { icon: Package, title: 'Unopened Only', desc: 'Products must be sealed', color: 'cyan' },
              { icon: RotateCcw, title: 'Full Refund', desc: '5-7 days processing', color: 'coral' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-800/50 rounded-2xl p-6 text-center border border-slate-700/50">
                <div className={`w-14 h-14 bg-${item.color}-600/20 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`w-7 h-7 text-${item.color}-400`} />
                </div>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Main Content */}
          <div className="bg-slate-800/50 rounded-3xl p-8 lg:p-10 border border-slate-700/50">
            <h2 className="font-display text-2xl text-white mb-6">Return Policy Details</h2>
            
            <p className="text-slate-400 leading-relaxed mb-10">
              At Keto Vybe, we want you to be completely satisfied with your purchase. If you're not happy for any reason, we offer a simple 30-day return policy.
            </p>

            {/* Eligible Items */}
            <div className="mb-10">
              <h3 className="flex items-center gap-3 font-semibold text-white text-lg mb-5">
                <div className="w-8 h-8 bg-vybe-600/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-vybe-400" />
                </div>
                Eligible for Return
              </h3>
              <ul className="space-y-3 pl-11">
                {[
                  'Unopened and sealed supplements',
                  'Products in original packaging',
                  'Items returned within 30 days of delivery',
                  'Products with proof of purchase',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-vybe-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Not Eligible Items */}
            <div className="mb-10">
              <h3 className="flex items-center gap-3 font-semibold text-white text-lg mb-5">
                <div className="w-8 h-8 bg-coral-600/20 rounded-lg flex items-center justify-center">
                  <XCircle className="w-4 h-4 text-coral-400" />
                </div>
                Not Eligible for Return
              </h3>
              <ul className="space-y-3 pl-11">
                {[
                  'Opened or used supplements',
                  'Products without original packaging',
                  'Items returned after 30 days',
                  'Products damaged by customer',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                    <XCircle className="w-4 h-4 text-coral-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* How to Return */}
            <div className="mb-10">
              <h3 className="font-semibold text-white text-lg mb-5">How to Return</h3>
              <div className="space-y-3">
                {[
                  { step: '1', title: 'Contact Us', desc: `Email ${CONTACT_INFO.email} with your order number` },
                  { step: '2', title: 'Get Approval', desc: 'We\'ll send return instructions within 24-48 hours' },
                  { step: '3', title: 'Ship Return', desc: 'Pack items securely and ship to our return address' },
                  { step: '4', title: 'Get Refund', desc: 'Refund processed within 5-7 business days' },
                ].map((step) => (
                  <div key={step.step} className="flex gap-4 p-4 bg-slate-900/50 rounded-xl">
                    <div className="w-10 h-10 bg-gradient-to-br from-vybe-600 to-vybe-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{step.step}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{step.title}</h4>
                      <p className="text-slate-400 text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-slate-900/50 border border-vybe-500/30 rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-vybe-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-white mb-1">Important Note</h4>
                  <p className="text-slate-400 text-sm">
                    Return shipping costs are the customer's responsibility unless the item is defective or we made an error.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-10 bg-slate-800/50 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-slate-700/50">
            <div>
              <h3 className="font-semibold text-white">Have Questions?</h3>
              <p className="text-slate-400 text-sm">Our team is ready to help.</p>
            </div>
            <Link 
              to="/contact"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full"
            >
              Contact Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReturnsPage;
